import { categorizeTaxStatus, type StatusClass } from '../../../../GridStatuses';
import styles from './K1HierarchyNode.module.css';
import type { K1HierarchyNodeData, NodeBadgeState } from './K1HierarchyNode.types';

const STATUS_DOT_CLASS: Partial<Record<StatusClass, string>> = {
  imported: styles.nodeDotImported,
  'not-imported': styles.nodeDotNotImported,
  error: styles.nodeDotImportFailed,
  importing: styles.nodeDotImporting,
  'action-pending': styles.nodeDotActionPending,
};

function truncateStatusMessage(value: unknown, maxLength = 280) {
  const text = typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value);
  const trimmed = text.trim();
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength - 1)}...` : trimmed;
}

function hasTaxMessage(value: unknown) {
  return value != null && value !== 0 && value !== '' && value !== '0';
}

function getTaxStatusText(raw: string | null | undefined, statusClass: StatusClass) {
  if (!String(raw ?? '').trim()) return '';

  if (statusClass === 'imported') return 'Imported';
  if (statusClass === 'not-imported') return 'Not Imported';
  if (statusClass === 'error') return 'Import Failed';
  if (statusClass === 'importing') return 'Importing...';

  return String(raw);
}

function getStatusTooltip(data: K1HierarchyNodeData, statusClass: StatusClass) {
  if (statusClass === 'importing') return 'This should take ~1min to complete';
  if (statusClass !== 'error') return undefined;

  return hasTaxMessage(data.docData?.TaxMessage)
    ? truncateStatusMessage(data.docData?.TaxMessage)
    : 'Import failed.';
}

export function getNodeBadgeState(data: K1HierarchyNodeData): NodeBadgeState {
  const statusClass = categorizeTaxStatus(data.taxStatus);
  const statusText = getTaxStatusText(data.taxStatus, statusClass);
  const statusTooltip = getStatusTooltip(data, statusClass);
  const hasImportData = !!statusText && !data.isCurrentClient && !data.isReadOnly;
  const hasReturnId = !!data.selectedReturnId;
  const canInteract = !data.isReadOnly && !data.isCurrentClient && !!data.isInternalK1;
  const isSelected = !!data.isSelected;
  const statusDotClassName = STATUS_DOT_CLASS[statusClass] ?? styles.nodeDotNotImported;

  const badgeClassName = !hasReturnId
    ? styles.nodeCheckBadgeInactive
    : isSelected
    ? styles.nodeCheckBadgeSelected
    : styles.nodeCheckBadgeSelectable;

  const badgeTitle = !hasReturnId
    ? 'Assign a Return ID to select this source for import'
    : isSelected
    ? 'Click to deselect'
    : 'Click to select for import';

  return {
    badgeClassName,
    badgeTitle,
    canInteract,
    hasImportData,
    hasReturnId,
    isSelected,
    statusDotClassName,
    statusTooltip,
    statusText,
  };
}
